import { open_preview as open_file_preview } from "@/utils/file_preview";
import { clear_file_input } from "@/views/home/home_helper.js";

export function extract_year(label) {
  const match = String(label || "").match(/\b(19|20)\d{2}\b/);
  return match ? Number(match[0]) : 0;
}

export function count_documents(folders = []) {
  return folders.reduce((sum, folder) => {
    const folder_files = Array.isArray(folder.files) ? folder.files.length : 0;
    const subfolder_files = Array.isArray(folder.subfolders) ? count_documents(folder.subfolders) : 0;
    return sum + folder_files + subfolder_files;
  }, 0);
}

export function count_folders(folders = []) {
  return folders.reduce((sum, folder) => {
    const subfolder_count = Array.isArray(folder.subfolders) ? count_folders(folder.subfolders) : 0;
    return sum + 1 + subfolder_count;
  }, 0);
}

export function get_folder_created_time(folder) {
  if (!folder) {
    return 0;
  }

  const parsed_date = Date.parse(folder.date_created || folder.created_at || folder.created || "");
  return Number.isNaN(parsed_date) ? 0 : parsed_date;
}

export function get_latest_folder_id(folders = []) {
  if (!folders.length) {
    return null;
  }

  const dated_folders = folders.filter((folder) => get_folder_created_time(folder));

  if (!dated_folders.length) {
    return folders[0].id;
  }

  return dated_folders.reduce((latest_folder, folder) => (
    get_folder_created_time(folder) > get_folder_created_time(latest_folder)
      ? folder
      : latest_folder
  ), dated_folders[0]).id;
}

function choose_folder_value(updated_value, existing_value, fallback = undefined) {
  return updated_value || existing_value || fallback;
}

export function build_updated_folder(existing_folder = {}, updated_folder = {}) {
  return {
    id: choose_folder_value(updated_folder.id, existing_folder.id),
    folder_name: choose_folder_value(updated_folder.folder_name, existing_folder.folder_name, ""),
    date_created: choose_folder_value(updated_folder.date_created, existing_folder.date_created),
    created_at: choose_folder_value(updated_folder.created_at, existing_folder.created_at),
    created: choose_folder_value(updated_folder.created, existing_folder.created),
    subfolders: choose_folder_value(updated_folder.subfolders, existing_folder.subfolders, []),
    files: choose_folder_value(updated_folder.files, existing_folder.files, []),
  };
}

export function get_file_url(file_item) {
  return file_item?.file?.s3_url || file_item?.file?.file_url || file_item?.file?.url || "";
}

export function get_file_name(file_item) {
  if (file_item?.file?.name) {
    return file_item.file.name;
  }

  const file_url = get_file_url(file_item);
  if (!file_url) {
    return "Document";
  }

  const clean_url = decodeURIComponent(file_url).split("?")[0];
  return clean_url.substring(clean_url.lastIndexOf("/") + 1);
}

export function folder_summary(folder) {
  const document_count = Array.isArray(folder.files) ? folder.files.length : 0;
  const subfolder_count = Array.isArray(folder.subfolders) ? folder.subfolders.length : 0;

  if (document_count && subfolder_count) {
    return `${document_count} files - ${subfolder_count} folders`;
  }

  if (document_count) {
    return `${document_count} files - Report, infographic, data, methodology`;
  }

  if (subfolder_count) {
    return `${subfolder_count} folders available`;
  }

  return "No items in this folder yet";
}

function update_folder_in_tree(folders, updated_folder) {
  const index = folders.findIndex((folder) => folder.id === updated_folder.id);
  if (index !== -1) {
    const existing_folder = folders.find((folder) => folder.id === updated_folder.id);
    folders.splice(index, 1, build_updated_folder(existing_folder, updated_folder));
    return true;
  }

  return folders.some((folder) => update_folder_in_tree(folder.subfolders || [], updated_folder));
}

function remove_folder_from_tree(folders, folder_id) {
  const index = folders.findIndex((folder) => folder.id === folder_id);
  if (index !== -1) {
    folders.splice(index, 1);
    return true;
  }

  return folders.some((folder) => remove_folder_from_tree(folder.subfolders || [], folder_id));
}

export const assessment_tool_methods = {
  clear_warehouse_file_input(event = null) {
    clear_file_input(event || { target: this.$refs.warehouse_file });
  },
  is_latest_folder(folder) {
    return Boolean(folder?.id) && folder.id === this.latest_folder_id;
  },
  folder_summary(folder) {
    return folder_summary(folder);
  },
  folder_badge(folder) {
    return this.is_latest_folder(folder) ? "Latest" : "Archive";
  },
  open_file(file_item) {
    const file_url = this.get_file_url(file_item);
    if (!file_url) {
      return;
    }

    this.preview_key += 1;
    open_file_preview(this, {
      file: {
        ...file_item.file,
        url: file_url,
        name: this.get_file_name(file_item),
      },
    }, "assessment_preview_modal");

    if (this.preview_type === "other") {
      this.loading_preview = false;
    }
  },
  on_content_loaded() {
    this.loading_preview = false;
  },
  close_preview() {
    this.preview_url = null;
    this.preview_type = null;
    this.loading_preview = false;
  },
  update_folder_in_tree(folders, updated_folder) {
    return update_folder_in_tree(folders, updated_folder);
  },
  update_folder_in_stack(updated_folder) {
    const index = this.folder_stack.findIndex((folder) => folder.id === updated_folder.id);
    if (index !== -1) {
      const existing_folder = this.folder_stack.find((folder) => folder.id === updated_folder.id);
      this.folder_stack.splice(index, 1, build_updated_folder(existing_folder, updated_folder));
    }
  },
  remove_folder_from_tree(folders, folder_id) {
    return remove_folder_from_tree(folders, folder_id);
  },
  remove_folder_from_stack(folder_id) {
    const index = this.folder_stack.findIndex((folder) => folder.id === folder_id);
    if (index !== -1) {
      this.folder_stack = this.folder_stack.slice(0, index);
    }
  },
  update_file_in_current_folder(updated_file) {
    const file_index = this.current_files.findIndex((file) => file.id === updated_file.id);
    if (file_index !== -1) {
      this.current_files.splice(file_index, 1, updated_file);
    }

    const active_folder = this.folder_stack[this.folder_stack.length - 1];
    const active_file_index = active_folder?.files?.findIndex((file) => file.id === updated_file.id);
    if (active_file_index !== undefined && active_file_index > -1) {
      active_folder.files.splice(active_file_index, 1, updated_file);
    }
  },
  remove_file_from_current_folder(file_id) {
    this.current_files = this.current_files.filter((file) => file.id !== file_id);

    const active_folder = this.folder_stack[this.folder_stack.length - 1];
    if (active_folder?.files) {
      active_folder.files = active_folder.files.filter((file) => file.id !== file_id);
    }
  },
  get_file_url(file_item) {
    return get_file_url(file_item);
  },
  get_file_name(file_item) {
    return get_file_name(file_item);
  },
};
